// La cotización: un REGISTRO del mini CRM (estado, vendedor, cliente, montos) con un BORRADOR editable y VERSIONES
// inmutables (cada PDF generado es una versión). "Todos ven todo" (Diego).
import { z } from 'zod';
import { Etapa, Fecha, FechaHora, FormatoFamilia, Id, OpcionId, Pesos, Tarifa, Texto, TipoProyecto } from './basicos.ts';
import { Cuestionario, ETIQUETA_REGIMEN_FISCAL, etapaDe, formatoPara } from './cuestionario.ts';
import type { RegimenFiscal } from './cuestionario.ts';
import { ArchivoSubido } from './archivos.ts';
import { Lecturas } from './recibo.ts';
import { CostoAdicional } from './costos.ts';
import { ParametrosCalculo, ResultadoMotor, Semaforo } from './opciones.ts';
import type { Rentabilidad } from './opciones.ts';
import { Aviso, esAvisoFinsus, rojosPendientes, soloDescuentosQueAutorizanInternos } from './avisos.ts';
import { ArchivoLeido, RevisionIA } from './ia.ts';
import { CalculoAmpliacion, PiezasAmpliacion } from './ampliacion-baterias.ts';
import { EdicionCampo } from './edicion.ts';
import { puede, puedeAutorizarDescuento, type Usuario } from './usuario.ts';
import { ResumenListaCompra, VentaCotizacion } from './lista-compra.ts';
import { PromoCotizacion } from './promo.ts';
import { SeguimientoCrm } from './seguimiento.ts';

export const ESTADOS_COTIZACION = ['borrador', 'enviada', 'seguimiento', 'visita_agendada', 'ganada', 'perdida', 'cancelada'] as const;
export const EstadoCotizacion = z.enum(ESTADOS_COTIZACION);
export type EstadoCotizacion = z.infer<typeof EstadoCotizacion>;

export const ETIQUETA_ESTADO: Readonly<Record<EstadoCotizacion, string>> = Object.freeze({
  borrador: 'Borrador',
  enviada: 'Enviada',
  seguimiento: 'En seguimiento',
  visita_agendada: 'Visita agendada',
  ganada: 'Ganada',
  perdida: 'Perdida',
  cancelada: 'Cancelada',
});

/** ADENDA A0b · aprobación de Diego para un precio que la pide (descuento mayor al tope, margen debajo del mínimo,
 *  renglón sin monto). Vale SOLO para los precios que Diego vio: `firma` (firmaPrecios) se compara con la del resultado
 *  vigente; si el vendedor cambia el precio después, la aprobación ya no aplica. Nunca se edita a mano (PATCH): solo por
 *  sus rutas (pedir: cualquier vendedor que edita la cotización; aprobar o negar: solo el director). */
export const AprobacionPrecio = z.strictObject({
  estado: z.enum(['pendiente', 'aprobada', 'rechazada']),
  firma: z.string().min(1).max(400),
  pedida_por: Id,
  pedida_en: FechaHora,
  /** Lo que el vendedor le dice a Diego al pedirla. */
  nota_pedido: z.string().trim().max(500).nullable(),
  resuelta_por: Id.nullable(),
  resuelta_en: FechaHora.nullable(),
  /** Lo que Diego contestó. */
  nota: z.string().trim().max(500).nullable(),
});
export type AprobacionPrecio = z.infer<typeof AprobacionPrecio>;

/** Firma de los precios de un resultado: lista y contado sin IVA de cada opción + la versión de la tabla. Si cambia
 *  cualquiera, una aprobación de Diego deja de valer. Con baterías (E0 baterías por opción, 24-oct) firma también el nivel
 *  recomendado y lista y contado sin IVA de cada nivel (`-` = sin precio): subir o bajar baterías cambia lo que se cobra.
 *  SIN baterías la cadena es la MISMA de antes del 24-oct (no se caen las aprobaciones de paneles ya dadas). */
export function firmaPrecios(r: {
  opciones: readonly { id: OpcionId; precio: { lista_sin_iva: number; contado_sin_iva: number } }[];
  tabla_precios_version?: number | undefined;
  respaldo?: { recomendado: string; niveles: readonly { id: string; precio: { lista_sin_iva: number; contado_sin_iva: number } | null }[] } | null | undefined;
}): string {
  const dinero = (p: { lista_sin_iva: number; contado_sin_iva: number }) => `${p.lista_sin_iva.toFixed(2)}:${p.contado_sin_iva.toFixed(2)}`;
  const baterias = r.respaldo
    ? [`b:${r.respaldo.recomendado}`, ...r.respaldo.niveles.map((n) => `b.${n.id}:${n.precio ? dinero(n.precio) : '-'}`)]
    : [];
  return [...r.opciones.map((o) => `${o.id}:${dinero(o.precio)}`), ...baterias, `t${r.tabla_precios_version ?? 0}`].join('|');
}

/** AUTORIZACIONES (Diego, 11-oct; 12-oct): ¿por qué ESTE usuario NO puede aprobar el precio de ESTA cotización? null = sí
 *  puede. Diego (aprobar_precios) aprueba todo. Miguel y Juan Carlos (autorizar_descuentos) solo un descuento
 *  (CODIGOS_QUE_AUTORIZAN_INTERNOS) y nunca el de una cotización suya ("nadie se autoriza a sí mismo"). Lo usan el servidor
 *  (al aprobar y al generar el PDF), el simulado y la interfaz. */
export function motivoNoAutoriza(u: Pick<Usuario, 'id' | 'rol' | 'activo'> & { optimus?: boolean | undefined }, c: { vendedor_id: string; creada_por?: string | null | undefined }, avisos: readonly Aviso[]): string | null {
  if (puede(u, 'aprobar_precios')) return null;
  // Verificación adversarial (13-oct): un asesor de Optimus no autoriza (misma regla que el tope de internos)
  if (!puedeAutorizarDescuento(u)) return 'Este precio solo lo aprueba Diego (o Miguel o Juan Carlos si es un descuento).';
  // Verificación del 13-oct: quien la CAPTURÓ a nombre de otro vendedor tampoco se autoriza su propio descuento.
  if (c.vendedor_id === u.id || c.creada_por === u.id) return 'Nadie se autoriza a sí mismo: pide el OK a Diego o al otro asesor.';
  if (!soloDescuentosQueAutorizanInternos(avisos)) return 'Este precio queda debajo del piso o del margen mínimo (o le falta un monto): ese OK solo lo da Diego.';
  return null;
}

/** ¿Hay una aprobación de Diego que cubra los precios de AHORA? */
export function aprobacionVigente(c: { resultado: ResultadoMotor | null; aprobacion?: AprobacionPrecio | null | undefined }): AprobacionPrecio | null {
  const a = c.aprobacion;
  if (!a || a.estado !== 'aprobada' || !c.resultado) return null;
  return a.firma === firmaPrecios(c.resultado) ? a : null;
}

/** Todo lo editable de una cotización. `resultado` lo calcula el motor (nunca se edita a mano): el servidor lo
 *  recalcula en la misma petición después de cada cambio, así nunca queda viejo. */
export const ContenidoCotizacion = z.strictObject({
  cuestionario: Cuestionario,
  archivos: z.array(ArchivoSubido),
  lecturas: Lecturas,
  costos_adicionales: z.array(CostoAdicional),
  parametros: ParametrosCalculo,
  resultado: ResultadoMotor.nullable(),
  revision_ia: RevisionIA.nullable(),
  avisos: z.array(Aviso),
  /** Contrato v2: estado POR ARCHIVO de la última lectura con IA (ResultadoLeerRecibos.archivos). Lo pone el servidor
   *  en cada lectura (reemplaza la anterior); nunca se edita a mano. Ausente = borrador de antes de v2 o sin leer. */
  archivos_leidos: z.array(ArchivoLeido).max(60).optional(),
  /** ADENDA A0b: la última aprobación pedida a Diego para el precio (ausente/null = nunca se pidió). */
  aprobacion: AprobacionPrecio.nullable().optional(),
  /** Ampliación de baterías (tipo «ampliacion_baterias», 27-oct-2026): lo que captura el vendedor. Solo en ese tipo. */
  piezas: PiezasAmpliacion.nullable().optional(),
  /** Ampliación de baterías: partidas, precios y margen que calcula el servidor (como `resultado` en los solares; nunca se edita). */
  calculo_piezas: CalculoAmpliacion.nullable().optional(),
}).superRefine((c, ctx) => {
  const esAmpliacion = c.cuestionario.tipo === 'ampliacion_baterias';
  if (!esAmpliacion && (c.piezas || c.calculo_piezas)) ctx.addIssue({ code: 'custom', path: ['piezas'], message: 'La captura de piezas solo va en una ampliación de baterías.' });
  if (esAmpliacion && c.resultado) ctx.addIssue({ code: 'custom', path: ['resultado'], message: 'Una ampliación de baterías no pasa por el motor solar: no lleva resultado.' });
  // ADENDA A0 también en el borrador: con un resultado A0, todo aviso de FINSUS (de cualquier origen) es "info".
  if (c.resultado && c.resultado.opciones.some((o) => o.finsus_referencia !== undefined))
    for (const [i, a] of c.avisos.entries())
      if (a.nivel !== 'info' && esAvisoFinsus(a))
        ctx.addIssue({ code: 'custom', path: ['avisos', i, 'nivel'], message: `ADENDA A0: el aviso ${a.codigo} es de FINSUS y vino "${a.nivel}" (origen ${a.origen}): FINSUS es solo informativo.` });
});
export type ContenidoCotizacion = z.infer<typeof ContenidoCotizacion>;

/** MULTISITIO: a qué proyecto de varios sitios pertenece la cotización y cómo se llama la sucursal. */
export const SitioDeProyecto = z.strictObject({
  grupo_id: Id,
  /** Nombre de la sucursal tal como lo dice el vendedor («Sucursal Norte», «Planta Navojoa»). */
  sitio: Texto(2, 40),
});
export type SitioDeProyecto = z.infer<typeof SitioDeProyecto>;

/** MULTISITIO: tope de sucursales que se crean de una vez desde «Nueva cotización». */
// ADENDA z67-2 (Diego, 30-oct-2026): de 12 a 15 (Perisur en un solo proyecto), MEDIDO con el PDF real de 15 sucursales de nombre
// largo (paquetes/pdf/src/real.test.ts, «15 sucursales»): si la hoja no las aguanta, la plantilla truena (N15) y no sale cortado.
export const SITIOS_MAX = 15;

/** LÍMITE LEGAL (ADENDA, Diego 13-oct-2026 22:30: «si no se puede, no se puede»): con 700 kW o más de inversores (CA) ya no
 *  es generación distribuida. El motor lo lee de sus datos de regulación (regulacion-gd.json → umbrales.gd_kw) y una prueba del
 *  motor revisa que sea este MISMO número. El PDF de todas las sucursales deja fuera a la sucursal cuya Exacta llega aquí. */
export const LIMITE_GD_KW_CA = 700;

/** PDF de todas (regla «y» también para el formato, verificación adversarial del 14-oct): el porqué de la sucursal que la
 *  plantilla no pudo dibujar en el paquete y quedó fuera (lo ve el vendedor; Diego recibe el detalle del formato). */
export const PORQUE_FUERA_POR_FORMATO = 'la plantilla del PDF no la pudo dibujar junto con las demás (problema del formato, no de sus datos: ya se le avisó a Diego)';

/** MULTISITIO: la palabra con que la búsqueda del CRM encuentra todas las sucursales de un proyecto (va en la columna de
 *  búsqueda de SQLite y de Datastore; las dos la arman con columnasCotizacion). Ids en minúsculas: la búsqueda se normaliza. */
export function claveBusquedaGrupo(grupoId: string): string {
  return `sitios-${grupoId.toLowerCase()}`;
}

/** MULTISITIO: lo que se agrega al texto de búsqueda de la cotización (nombre de la sucursal y la clave del grupo). */
export function textoBusquedaMultisitio(c: { multisitio?: SitioDeProyecto | null | undefined }): string {
  return c.multisitio ? ` ${c.multisitio.sitio} ${claveBusquedaGrupo(c.multisitio.grupo_id)}` : '';
}

/** MULTISITIO: lo que falta (dicho a quien cotiza, sin rodeos). */
export const AVISO_PDF_MULTISITIO =
  'Cotiza cada sucursal aquí (sus recibos, su tarifa y sus opciones). Con todas calculadas, «PDF de todas las sucursales» las junta en una sola propuesta.';

/** ADENDA z26 (Diego, 21-oct-2026): una propuesta multisitio = UN cliente que paga → el PDF de todas usa el régimen fiscal de
 *  la PRINCIPAL (su FINSUS y su deducción). Si otra sucursal que entra trae otro régimen, el VENDEDOR ve UN aviso amarillo que
 *  la nombra (no bloquea el PDF, no se imprime en el PDF del cliente y no se repite: cada PDF de todas reemplaza al anterior). */
export const PREFIJO_AVISO_REGIMEN_MULTISITIO = 'Régimen fiscal distinto entre sucursales';
export interface SitioConRegimen {
  sitio: string;
  folio: string;
  regimen: RegimenFiscal | null | undefined;
}
export function avisoRegimenMultisitio(principal: SitioConRegimen, otras: readonly SitioConRegimen[]): string | null {
  // «No sé todavía» y sin régimen son lo mismo para el PDF (crédito de persona, sin deducción)
  const clave = (r: SitioConRegimen['regimen']) => (r && r !== 'no_se' ? r : 'no_se');
  const nombre = (r: SitioConRegimen['regimen']) => (clave(r) === 'no_se' ? 'sin régimen definido' : ETIQUETA_REGIMEN_FISCAL[clave(r)].replace(/ \(.*\)$/, '').toLowerCase());
  const distintas = otras.filter((x) => clave(x.regimen) !== clave(principal.regimen));
  if (!distintas.length) return null;
  return `${PREFIJO_AVISO_REGIMEN_MULTISITIO}: ${distintas.map((x) => `«${x.sitio}» (${x.folio}, ${nombre(x.regimen)})`).join(', ')} ${distintas.length > 1 ? 'tienen' : 'tiene'} otro régimen que la principal «${principal.sitio}» (${principal.folio}, ${nombre(principal.regimen)}). El PDF de todas usa el de la principal (su FINSUS y su deducción); si una sucursal la paga otra razón social, sácale su PDF individual.`;
}

export const CrmExternoEstado = z.strictObject({
  enviado: z.boolean(),
  id_externo: z.string().max(120).nullable(),
  ultimo_intento: FechaHora.nullable(),
  error: z.string().max(500).nullable(),
});
export type CrmExternoEstado = z.infer<typeof CrmExternoEstado>;

export const Cotizacion = z.strictObject({
  id: Id,
  /** SX-2026-0001 (lo asigna el repositorio, consecutivo por año, atómico). */
  folio: z.string().regex(/^SX-\d{4}-\d{4,6}$/),
  estado: EstadoCotizacion,
  vendedor_id: Id,
  // ---- Campos del CRM DERIVADOS del borrador (los pone resumenDesdeBorrador; nunca se escriben a mano) ----
  cliente_nombre: Texto(2, 60),
  ciudad: Texto(2, 40),
  tipo: TipoProyecto,
  etapa: Etapa,
  tarifa: Tarifa.nullable(),
  kwp_recomendada: z.number().positive().nullable(),
  /** Contado con IVA de la opción recomendada. */
  contado_recomendada: Pesos.nullable(),
  lista_recomendada: Pesos.nullable(),
  rojos_pendientes: z.int().nonnegative(),
  /** ADENDA A0b (11-oct): "en cuántos centavos por watt estamos cotizando" — contado SIN IVA de la recomendada entre sus
   *  watts, en dólares (con el TC del folio) y en pesos. Lo ven todos. Ausente = cotización de antes o sin resultado. */
  contado_usd_por_w: z.number().positive().nullable().optional(),
  contado_mxn_por_w: z.number().positive().nullable().optional(),
  /** SOLO DIRECTOR (vista.ts los quita para los demás): margen % después de comisión y utilidad esperada (contado sin
   *  IVA − costo) de la recomendada, para las columnas y totales del CRM. */
  margen_pct: z.number().nullable().optional(),
  utilidad_sin_iva: z.number().nullable().optional(),
  /** Semáforo de margen de la recomendada (Diego, 11-oct): lo ven el director y los asesores internos (SOLO el color);
   *  vista.ts lo quita a los externos. Ausente = cotización de antes o sin resultado. */
  semaforo_margen: Semaforo.nullable().optional(),
  // ----
  /** Desde cuándo está en su estado actual (lo pone el servidor al crear y en cada cambio de estado). Ausente =
   *  cotización de antes (se usa actualizada_en). Sirve para el seguimiento de 3 días de la ADENDA A0b. */
  estado_desde: FechaHora.optional(),
  version_actual: z.int().nonnegative(),
  borrador: ContenidoCotizacion,
  notas_crm: z.string().trim().max(2_000).nullable(),
  proximo_seguimiento: Fecha.nullable(),
  crm_externo: CrmExternoEstado,
  creada_en: FechaHora,
  creada_por: Id,
  actualizada_en: FechaHora,
  actualizada_por: Id,
  /** Concurrencia optimista: cada escritura sube 1. Toda petición que cambia algo manda la revisión que vio;
   *  si no coincide, CONFLICTO (409) y la interfaz recarga. */
  revision: z.int().nonnegative(),
  /** PAPELERA (Diego, 13-oct): quién la mandó a la papelera y cuándo. Ausente o null = NO está en la papelera (las
   *  cotizaciones guardadas antes no traen el campo y se leen así, sin migración). Una cotización en la papelera no sale
   *  en la lista, el conteo, los totales ni el seguimiento; no se edita; solo Diego la ve (filtro «Papelera») y la
   *  recupera. Su folio NO se reutiliza (siguienteFolio es un contador). */
  papelera: z
    .strictObject({ por: Id, nombre: z.string().trim().min(1).max(80), en: FechaHora })
    .nullable()
    .optional(),
  /** MULTISITIO (Diego, 13-oct, segunda tanda): esta cotización es UNA sucursal de un proyecto de varios sitios (p. ej.
   *  Crazy Motors con 2 sucursales). Cada sucursal es su propia cotización (sus recibos, su tarifa, sus opciones, calculada
   *  por el motor como siempre) y todas comparten `grupo_id`. Ausente o null = cotización de un solo sitio (todas las de
   *  antes; se leen así, sin migración). Lo pone el servidor al crear; no se edita a mano. */
  multisitio: SitioDeProyecto.nullable().optional(),
  /** «Hoy toca» (Diego, 2-nov): mensajes abiertos y qué pasó con el cliente. Ausente o null = nadie le ha dado seguimiento desde ahí. */
  seguimiento: SeguimientoCrm.nullable().optional(),
});
export type Cotizacion = z.infer<typeof Cotizacion>;
export type PapeleraCotizacion = NonNullable<Cotizacion['papelera']>;

/** ¿Está en la papelera? (ausente = no: documentos de antes del 13-oct). */
export function enPapelera(c: { papelera?: PapeleraCotizacion | null | undefined }): boolean {
  return !!c.papelera;
}

/** Mensaje cuando alguien intenta abrir o tocar una cotización que está en la papelera. */
export const MENSAJE_EN_PAPELERA = 'Está en la papelera; Diego la puede recuperar.';

/** PAPELERA (Diego, 13-oct): ¿puede `u` mandar ESTA cotización a la papelera? null = sí; si no, el porqué (en español).
 *  Diego (director) borra cualquiera; un vendedor (asesor o externo) solo las SUYAS y solo en Borrador. Lo hace
 *  cumplir el SERVIDOR (403); la pantalla usa la misma función para enseñar o no el botón. */
export function motivoNoMandaAPapelera(u: Pick<Usuario, 'id' | 'rol' | 'activo'>, c: { vendedor_id: string; estado: EstadoCotizacion; folio?: string; creada_por?: string; papelera?: PapeleraCotizacion | null | undefined }): string | null {
  if (enPapelera(c)) return `${c.folio ?? 'Esta cotización'} ya está en la papelera.`;
  if (puede(u, 'administrar_papelera')) return null;
  if (!puede(u, 'editar_cotizaciones')) return 'Tu usuario no puede borrar cotizaciones.';
  if (c.vendedor_id !== u.id) return `Solo puedes borrar tus propias cotizaciones: ${c.folio ?? 'esta'} es de otro vendedor. Pídele a Diego que la borre.`;
  // Verificación adversarial (13-oct): reasignarse un borrador ajeno y borrarlo. Solo la borra quien la CREÓ.
  if (c.creada_por !== undefined && c.creada_por !== u.id) return `Solo puedes borrar las cotizaciones que tú creaste: ${c.folio ?? 'esta'} la creó otra persona. Pídele a Diego que la borre.`;
  if (c.estado !== 'borrador') return `Solo puedes borrar tus cotizaciones en Borrador: ${c.folio ?? 'esta'} está en «${ETIQUETA_ESTADO[c.estado]}». Pídele a Diego que la borre.`;
  return null;
}

/** Renglón de la lista del CRM. `dias_sin_respuesta`: con estado "enviada", días completos (Hermosillo) desde que se
 *  envió sin otro cambio de estado; null en cualquier otro estado. Con DIAS_SIN_RESPUESTA o más, la lista la marca. */
export const ResumenCotizacion = Cotizacion.omit({ borrador: true }).extend({
  vendedor_nombre: Texto(1, 80),
  dias_sin_respuesta: z.int().nonnegative().nullable().optional(),
});
export type ResumenCotizacion = z.infer<typeof ResumenCotizacion>;

export const CamposDerivados = Cotizacion.pick({
  cliente_nombre: true,
  ciudad: true,
  tipo: true,
  etapa: true,
  tarifa: true,
  kwp_recomendada: true,
  contado_recomendada: true,
  lista_recomendada: true,
  rojos_pendientes: true,
  contado_usd_por_w: true,
  contado_mxn_por_w: true,
  margen_pct: true,
  utilidad_sin_iva: true,
  semaforo_margen: true,
});
export type CamposDerivados = z.infer<typeof CamposDerivados>;

/** Lo que cobra el PDF de una cotización, para el CRM y la lista de versiones (verificación adversarial del 14-oct: el CRM
 *  contaba la opción de PANELES aunque el PDF de baterías cobrara el nivel de baterías: $254,200 y verde contra $520,500).
 *  En el formato de baterías con niveles calculados, el NIVEL recomendado: su precio (en el híbrido y el aislado ya suma los
 *  paneles) y su rentabilidad; el kWp, el de la opción de paneles que suma (respaldo.hibrido.opcion; null en un respaldo, que
 *  no vende paneles); sin USD/W ni MXN/W (son de un sistema de paneles: con baterías no se promedian). Si ese nivel no tiene
 *  precio (la tabla no lo trae: el PDF no sale), los montos van en null, nunca los de los paneles. En todo lo demás, la opción
 *  de paneles recomendada. La usan las dos bases (resumenDesdeBorrador, resumenDeVersion) y el servidor simulado. */
export function loQueCobraElPdf(b: { cuestionario: Pick<Cuestionario, 'tipo' | 'tarifa'> & { recorte_punta?: Cuestionario['recorte_punta'] }; resultado: ResultadoMotor | null; calculo_piezas?: CalculoAmpliacion | null | undefined }): {
  kwp: number | null;
  contado: number | null;
  lista: number | null;
  usd_por_w: number | null;
  mxn_por_w: number | null;
  rentabilidad: Rentabilidad | null;
} {
  // Ampliación de baterías: lo que cobra su propuesta de piezas (contado y lista con IVA); su margen va aparte (margenPiezas).
  if (b.cuestionario.tipo === 'ampliacion_baterias') {
    const p = b.calculo_piezas ?? null;
    return { kwp: null, contado: p?.contado_con_iva ?? null, lista: p?.lista_con_iva ?? null, usd_por_w: null, mxn_por_w: null, rentabilidad: null };
  }
  const r = b.resultado;
  if (!r) return { kwp: null, contado: null, lista: null, usd_por_w: null, mxn_por_w: null, rentabilidad: null };
  // Recorte de punta (z54, 29-oct-2026): el PDF (horaria) cobra la batería grande, no las opciones de paneles (son de referencia).
  // Si no se ofrece (se_ofrece = false: el PDF no sale), no cobra nada: el CRM y la lista no enseñan el monto de una propuesta que no
  // puede salir (verificación de hoy70).
  if (formatoPara(b.cuestionario, r.tarifa) === 'horaria') {
    const k = r.recorte_punta?.se_ofrece ? r.recorte_punta : null;
    return { kwp: null, contado: k?.precio.contado ?? null, lista: k?.precio.lista ?? null, usd_por_w: null, mxn_por_w: null, rentabilidad: k?.rentabilidad ?? null };
  }
  const res = r.respaldo ?? null;
  if (res && formatoPara(b.cuestionario, r.tarifa) === 'baterias') {
    const nivel = res.niveles.find((n) => n.id === res.recomendado) ?? null;
    const paneles = res.hibrido ? (r.opciones.find((o) => o.id === res.hibrido!.opcion) ?? null) : null;
    return {
      kwp: paneles?.kwp ?? null,
      contado: nivel?.precio?.contado ?? null,
      lista: nivel?.precio?.lista ?? null,
      usd_por_w: null,
      mxn_por_w: null,
      rentabilidad: (nivel?.precio && nivel.rentabilidad) || null,
    };
  }
  const rec = r.opciones.find((o) => o.id === r.recomendada) ?? null;
  return {
    kwp: rec?.kwp ?? null,
    contado: rec?.precio.contado ?? null,
    lista: rec?.precio.lista ?? null,
    usd_por_w: rec?.precio.contado_sin_iva_usd_por_w ?? null,
    mxn_por_w: rec?.precio.contado_sin_iva_por_w ?? null,
    rentabilidad: rec?.rentabilidad ?? null,
  };
}

/** Los campos del CRM salen SIEMPRE del borrador con esta función (el servidor la llama en cada escritura):
 *  así la lista del CRM no puede contar otra historia que la cotización (ni que su PDF: loQueCobraElPdf). */
export function resumenDesdeBorrador(b: ContenidoCotizacion): CamposDerivados {
  const r = b.resultado;
  const pdf = loQueCobraElPdf(b);
  const tarifaCuestionario = b.cuestionario.tarifa === 'no_se' ? null : b.cuestionario.tarifa;
  return {
    cliente_nombre: b.cuestionario.cliente.nombre,
    ciudad: b.cuestionario.cliente.ciudad,
    tipo: b.cuestionario.tipo,
    etapa: etapaDe(b.cuestionario),
    tarifa: r?.tarifa ?? b.lecturas.A?.tarifa ?? tarifaCuestionario,
    kwp_recomendada: pdf.kwp,
    contado_recomendada: pdf.contado,
    lista_recomendada: pdf.lista,
    rojos_pendientes: rojosPendientes(b.avisos).length,
    contado_usd_por_w: pdf.usd_por_w,
    contado_mxn_por_w: pdf.mxn_por_w,
    margen_pct: pdf.rentabilidad?.margen_contado_pct ?? margenPiezas(b)?.margen_pct ?? null,
    utilidad_sin_iva: pdf.rentabilidad?.utilidad_contado_sin_iva ?? margenPiezas(b)?.utilidad_sin_iva ?? null,
    semaforo_margen: pdf.rentabilidad?.semaforo ?? margenPiezas(b)?.semaforo ?? null,
  };
}

/** Ampliación de baterías: margen, utilidad y semáforo para el CRM (verde si deja el margen de la regla; por el redondeo hacia
 *  arriba siempre lo deja: un rojo aquí es un bug que se tiene que ver). */
export function margenPiezas(b: Pick<ContenidoCotizacion, 'cuestionario' | 'calculo_piezas'>): { margen_pct: number; utilidad_sin_iva: number; semaforo: Semaforo } | null {
  const p = b.cuestionario.tipo === 'ampliacion_baterias' ? (b.calculo_piezas ?? null) : null;
  if (!p || p.margen_pct === undefined || p.utilidad_sin_iva === undefined || !p.regla) return null;
  return { margen_pct: p.margen_pct, utilidad_sin_iva: p.utilidad_sin_iva, semaforo: p.margen_pct + 0.005 >= p.regla.margen_pct ? 'verde' : 'rojo' };
}

export const AvisoRojoConfirmado = z.strictObject({ aviso_id: Id, codigo: z.string(), mensaje: z.string(), nota: Texto(3, 500) });
export type AvisoRojoConfirmado = z.infer<typeof AvisoRojoConfirmado>;

export const PdfVersion = z.strictObject({
  generado_en: FechaHora,
  formato: FormatoFamilia,
  /** Folio impreso en el PDF (SX-2026-0001, o SX-2026-0001-V2 desde la segunda versión). */
  folio_pdf: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9-]{1,30}$/),
  /** `anexo` (etapa "Conectar"): el anexo técnico que el formato industrial imprime en SU PROPIO PDF (adenda A1:
   *  "industrial 5 + anexo"). Ausente en versiones de antes; null = el formato no lleva anexo. */
  archivos: z.strictObject({ pdf: Id, portada: Id.nullable(), avisos: Id, json_v1: Id, anexo: Id.nullable().optional() }),
  /** Avisos del generador para Diego (.avisos.txt): no se mandan al cliente. */
  avisos_generador: z.array(z.string()),
  avisos_rojos_confirmados: z.array(AvisoRojoConfirmado),
});
export type PdfVersion = z.infer<typeof PdfVersion>;

export const VersionCotizacion = z.strictObject({
  cotizacion_id: Id,
  numero: z.int().min(1),
  creada_en: FechaHora,
  creada_por: Id,
  motivo: Texto(2, 300),
  contenido: ContenidoCotizacion,
  pdf: PdfVersion.nullable(),
});
export type VersionCotizacion = z.infer<typeof VersionCotizacion>;

export const ResumenVersion = VersionCotizacion.omit({ contenido: true }).extend({
  kwp_recomendada: z.number().positive().nullable(),
  contado_recomendada: Pesos.nullable(),
  /** Promo de contado con la que salió ESTA versión (promoDeVersion de su contenido): la que repite su WhatsApp. Ausente = sin promo. */
  promo: PromoCotizacion.optional(),
});
export type ResumenVersion = z.infer<typeof ResumenVersion>;

export const TIPOS_EVENTO = [
  'creada',
  'editada',
  'estado',
  'vendedor',
  'archivo_subido',
  'archivo_quitado',
  'lectura_ia',
  'correccion_ia',
  'costo_agregado',
  'costo_quitado',
  'calculo',
  'revision_ia',
  'aviso_resuelto',
  'version',
  'pdf',
  'pdf_fallo',
  'crm_externo',
  /** ADENDA A0b: se pidió, dio o negó el OK de Diego para el precio. */
  'aprobacion',
  /** Etapa 4 (Diego, 12-oct): el vendedor revisó y AUTORIZÓ la tabla leída de los recibos (con o sin correcciones). */
  'lectura_autorizada',
  /** PAPELERA (Diego, 13-oct): se mandó a la papelera o se recuperó (quién y cuándo). */
  'papelera',
] as const;
export const TipoEvento = z.enum(TIPOS_EVENTO);
export type TipoEvento = z.infer<typeof TipoEvento>;

/** Contrato v2: cambio de estado o de vendedor CON NOMBRES (lo que la persona lee), además del id. */
export const CambioEvento = z.strictObject({
  campo: z.enum(['estado', 'vendedor']),
  /** id del estado o del usuario (null = no había: vendedor de una cotización nueva). */
  antes: z.string().max(64).nullable(),
  despues: z.string().max(64),
  /** "Borrador" / "Miguel Cota": lo que se enseña. Con campo "estado" DEBE ser ETIQUETA_ESTADO (el esquema lo exige). */
  antes_nombre: z.string().trim().min(1).max(80).nullable(),
  despues_nombre: z.string().trim().min(1).max(80),
});
export type CambioEvento = z.infer<typeof CambioEvento>;

/** Historial de la cotización: quién hizo qué y cuándo (incluye cada edición con su antes y después). */
export const Evento = z
  .strictObject({
    id: Id,
    cotizacion_id: Id,
    en: FechaHora,
    /** null = el sistema. */
    usuario_id: Id.nullable(),
    tipo: TipoEvento,
    detalle: z.string().max(1_000),
    ediciones: z.array(EdicionCampo).nullable(),
    /** Contrato v2: con tipo "estado" o "vendedor", el cambio con nombres (cambioDeEstado / cambioDeVendedor). */
    cambio: CambioEvento.nullable().optional(),
  })
  .superRefine((e, ctx) => {
    const c = e.cambio;
    if (!c) return;
    if (c.campo !== e.tipo) ctx.addIssue({ code: 'custom', path: ['cambio', 'campo'], message: `Un evento "${e.tipo}" no puede traer un cambio de ${c.campo}.` });
    if (c.campo === 'estado') {
      const despues = EstadoCotizacion.safeParse(c.despues);
      const antes = c.antes === null ? null : EstadoCotizacion.safeParse(c.antes);
      if (!despues.success || (antes && !antes.success)) ctx.addIssue({ code: 'custom', path: ['cambio'], message: 'Cambio de estado con un estado que no existe.' });
      else {
        if (c.despues_nombre !== ETIQUETA_ESTADO[despues.data])
          ctx.addIssue({ code: 'custom', path: ['cambio', 'despues_nombre'], message: `El nombre de "${despues.data}" es "${ETIQUETA_ESTADO[despues.data]}".` });
        if (antes && antes.success && c.antes_nombre !== ETIQUETA_ESTADO[antes.data])
          ctx.addIssue({ code: 'custom', path: ['cambio', 'antes_nombre'], message: `El nombre de "${antes.data}" es "${ETIQUETA_ESTADO[antes.data]}".` });
      }
    }
  });
export type Evento = z.infer<typeof Evento>;

/** Cambio de estado listo para el evento: { cambio, detalle } con los NOMBRES ("Estado: Borrador → Enviada."). */
export function cambioDeEstado(antes: EstadoCotizacion, despues: EstadoCotizacion): { cambio: CambioEvento; detalle: string } {
  return {
    cambio: { campo: 'estado', antes, despues, antes_nombre: ETIQUETA_ESTADO[antes], despues_nombre: ETIQUETA_ESTADO[despues] },
    detalle: `Estado: ${ETIQUETA_ESTADO[antes]} → ${ETIQUETA_ESTADO[despues]}.`,
  };
}

/** Cambio de vendedor listo para el evento ("Vendedor: Miguel Cota → Juan Carlos Cortés."). */
export function cambioDeVendedor(
  antes: { id: string; nombre: string } | null,
  despues: { id: string; nombre: string },
): { cambio: CambioEvento; detalle: string } {
  return {
    cambio: { campo: 'vendedor', antes: antes?.id ?? null, despues: despues.id, antes_nombre: antes?.nombre ?? null, despues_nombre: despues.nombre },
    detalle: antes ? `Vendedor: ${antes.nombre} → ${despues.nombre}.` : `Vendedor: ${despues.nombre}.`,
  };
}

/** Lo que devuelve GET /api/cotizaciones/:id. Nube, tanda 2: `eventos` son los ÚLTIMOS EVENTOS_POR_PAGINA del historial
 *  (del más viejo al más nuevo, como siempre); `eventos_antes` es el cursor para pedir los anteriores a la ruta
 *  historialCotizacion (null = no hay más; ausente = servidor de antes, que mandaba todo). */
export const DetalleCotizacion = z.strictObject({
  cotizacion: Cotizacion,
  versiones: z.array(ResumenVersion),
  eventos: z.array(Evento),
  eventos_antes: z.int().min(1).nullable().optional(),
  vendedor_nombre: Texto(1, 80),
  /** JUNTA, SEGUNDA TANDA (13-oct): «hecha por», quien la capturó (creada_por) cuando no es el vendedor a cuyo nombre sale.
   *  Se ve en la cotización y en el CRM; NUNCA sale en el PDF. Ausente = la capturó su propio vendedor (o servidor de antes). */
  creada_por_nombre: Texto(1, 80).optional(),
  /** ADENDA z59: en una Ganada, QUÉ se vendió (sin costos: la ven todos los que la abren). null = Ganada sin registro (de antes del
   *  30-oct o marcada desde otra pestaña); ausente = no está Ganada (o servidor de antes). Vive aparte (config «venta.<id>»), no en
   *  la cotización. */
  venta: VentaCotizacion.nullable().optional(),
  /** ADENDA z59: SOLO DIEGO (vista.ts lo quita a todos los demás): el resumen de la lista de compra congelada. */
  lista_compra: ResumenListaCompra.optional(),
});
export type DetalleCotizacion = z.infer<typeof DetalleCotizacion>;
